import { useEffect, useState, type FC } from 'react';
import { StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { SvgIconType } from '@thermenresorts/icons';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import Dimens from 'src/core/utils/Dimens';
import { useMessagesStore } from 'src/domain/stores/useMessagesStore';
import NavigationButton from 'src/presentation/components/molecules/NavigationButton';
import { useHeaderFade } from 'src/presentation/hooks/useHeaderFade';
import type { MainNavigation } from 'src/presentation/navigation/types/NavigationTypes';

interface Props {
	onImage?: boolean;
	style?: StyleProp<ViewStyle>;
}

const AccountButton: FC<Props> = ({ onImage = false, style }) => {
	const navigation = useNavigation<StackNavigationProp<MainNavigation>>();
	const { unreadCount, hasIntroducedUnreadBadge, markUnreadBadgeIntroduced } = useMessagesStore();
	const fade = useHeaderFade();

	// The pop-in belongs to the first time the badge appears, not to every screen that mounts the button.
	const [animatesBadgeIn] = useState(!hasIntroducedUnreadBadge);

	useEffect(() => {
		if (unreadCount > 0 && !hasIntroducedUnreadBadge) {
			markUnreadBadgeIntroduced();
		}
	}, [unreadCount, hasIntroducedUnreadBadge, markUnreadBadgeIntroduced]);

	const openAccount = () =>
		navigation.navigate(Constants.navigatorBottomTab, {
			screen: Constants.screenTabAccount,
			params: { screen: Constants.screenAccountDetail, pop: true },
		});

	const onWhiteStyle = useAnimatedStyle(() => ({ opacity: fade?.value ?? Dimens.opacity100 }));
	const overImageStyle = useAnimatedStyle(() => ({ opacity: Dimens.opacity100 - (fade?.value ?? Dimens.opacity0) }));

	const overImageButton = (
		<NavigationButton
			animatesBadgeIn={animatesBadgeIn}
			backgroundColor={Colors.headerButtonOnImage}
			badge={unreadCount}
			icon={SvgIconType.profile}
			iconColor={Colors.white}
			iconSize={Dimens.iconSize18}
			pressedBackgroundColor={Colors.headerButtonOnImagePressed}
			onPress={openAccount}
		/>
	);

	const onWhiteButton = (
		<NavigationButton
			animatesBadgeIn={animatesBadgeIn}
			badge={unreadCount}
			icon={SvgIconType.profile}
			iconSize={Dimens.iconSize18}
			onPress={openAccount}
		/>
	);

	if (!onImage) {
		return <View style={style}>{onWhiteButton}</View>;
	}

	if (!fade) {
		return <View style={style}>{overImageButton}</View>;
	}

	return (
		<View style={style}>
			<Animated.View style={onWhiteStyle}>{onWhiteButton}</Animated.View>
			<Animated.View style={[styles.overImage, overImageStyle]}>{overImageButton}</Animated.View>
		</View>
	);
};

const styles = StyleSheet.create({
	overImage: {
		position: 'absolute',
		top: Dimens.padding0,
		left: Dimens.padding0,
	},
});

export default AccountButton;
